import * as React from 'react';
import { ElementLoader, Tabs, ComplexValue } from 'ui';
import { deepEquals } from 'app/infrastructure/js';
import { hasSelectedValues, hasValues, hasExtraInfo, hasSelectedOptions } from './selectors';
import { UpdateAccessDataAction, ClearValidationAction } from 'app/components/access-panel/types';
import { TabsPanel } from 'app/components/tabs-panel';

import { AccessItem, AccessMode } from 'app/components/access-panel/shared/types';
import { ConfirmationBox } from 'app/components/confirmation-box/confirmation-box';

/** Some shared props of the component passed to withRoleBasedAccess method */
interface RoleBasedAccessExternalProps {

  formName: string;
  translation: any;
  initialValue: any;
  readOnly: boolean;
}

/** External props of RoleBasedAccess component - not passed down */
interface RoleBasedAccessOwnProps {

  roleSelected: boolean;
  roleValue: any;
  fetchingRoleValue: boolean;

  accessData: AccessItem;
  updateAccessData: UpdateAccessDataAction;
  clearValidation: ClearValidationAction;
}

interface RoleBasedAccessState {
  /** Specifies which tab is selected */
  tab: AccessMode;
}

export interface RoleBasedAccessCustomStateProps<S> {
  customState: S;
  changeCustomState: (state: S) => void;
}

// eslint:disable-next-line:max-func-body-length
export const withRoleBasedAccess = <P extends RoleBasedAccessExternalProps, S>(
  WrappedComponent: React.ComponentClass<P & RoleBasedAccessCustomStateProps<S>>) => {

  // fix to some issues during npm run build
  const RoleBasedComponent = WrappedComponent as any;

  return class extends React.Component<P & RoleBasedAccessOwnProps, RoleBasedAccessState> {

    public static defaultProps: Partial<RoleBasedAccessOwnProps> = {
      accessData: { mode: AccessMode.custom, customData: undefined, roleBaseData: undefined }
    };

    public state: Readonly<RoleBasedAccessState> = {
      tab: AccessMode.custom
    };

    public componentDidMount() {
      const { roleSelected, initialValue, formName, updateAccessData } = this.props;
      const mode = roleSelected && (!initialValue || !initialValue.length) ? AccessMode.role : AccessMode.custom;

      this.setState({ tab: mode });
      updateAccessData(formName, { mode, roleBaseData: undefined });
    }

    public componentDidUpdate(prevProps: P & RoleBasedAccessOwnProps) {

      const { roleSelected, roleValue, formName, accessData, updateAccessData } = this.props;

      // set access mode when user adds or removes role or changes role
      if (roleSelected !== prevProps.roleSelected || roleValue !== prevProps.roleValue) {

        const mode = roleSelected && (accessData.mode === AccessMode.role ||
          !accessData.customData ||
          (!hasSelectedValues(accessData.customData)
            && !hasValues(accessData.customData)
            && !hasExtraInfo(accessData.customData)
            && !hasSelectedOptions(accessData.customData)))
          ? AccessMode.role : AccessMode.custom;

        this.setState({ tab: mode });
        updateAccessData(formName, { mode, roleBaseData: undefined });
      }
    }

    private onChangeTab = (option: ComplexValue<number>) => this.setState({ tab: option.value });

    private onChangeMode = () => {
      const { formName, accessData, updateAccessData, clearValidation } = this.props;
      const mode = accessData.mode === AccessMode.role ? AccessMode.custom : AccessMode.role;
      const customData = mode === AccessMode.role ? undefined : accessData.roleBaseData;

      updateAccessData && updateAccessData(formName, { mode, customData });
      clearValidation && clearValidation(formName);
    };

    private onChangeCustomState = (state: S) => {
      const { formName, accessData, updateAccessData } = this.props;
      if (updateAccessData && !deepEquals(state, accessData.customData)) {
        updateAccessData(formName, { customData: state });
      }
    };

    private onChangeRoleBasedState = (state: S) => {
      const { formName, accessData, updateAccessData } = this.props;
      if (updateAccessData && !deepEquals(state, accessData.roleBaseData)) {
        updateAccessData(formName, { roleBaseData: state });
      }
    };

    private renderCustomMode() {

      const { readOnly, accessData, translation } = this.props;

      return <TabsPanel>
        {accessData.mode !== AccessMode.custom
          ? <ConfirmationBox
            text={translation.access_selection_panel_switch_custom_message_first || '' + ' ' +
              translation.access_selection_panel_switch_custom_message_second || ''}
            onClick={this.onChangeMode}
            buttonText={translation.access_selection_panel_switch_custom_button}
          />
          : <RoleBasedComponent {...this.props}
            customState={accessData.customData}
            changeCustomState={this.onChangeCustomState}
            readOnly={readOnly || accessData.mode !== AccessMode.custom} />}
      </TabsPanel>;
    }

    private renderRoleBasedMode() {
      const { formName, roleValue, fetchingRoleValue, accessData, translation } = this.props;

      return <TabsPanel>
        {fetchingRoleValue
          ? <ElementLoader />
          : <>
            {accessData.mode !== AccessMode.role && <ConfirmationBox
              text={translation.access_selection_panel_switch_role_based_message_first || '' + ' ' +
                translation.access_selection_panel_switch_role_based_message_second || ''}
              buttonText={translation.access_selection_panel_switch_role_based_button}
              onClick={this.onChangeMode}
            />
            }
            <RoleBasedComponent {...this.props}
              formName={`role-based-${formName}`}
              initialValue={roleValue}
              readOnly={true}
              customState={accessData.roleBaseData}
              changeCustomState={this.onChangeRoleBasedState} />
          </>
        }
      </TabsPanel >;
    }

    public render() {

      const { formName, roleSelected, readOnly, translation } = this.props;

      if (!roleSelected) {
        return this.renderCustomMode();
      }

      const tabsOptions = [
        { value: AccessMode.role, description: translation.role_base_access_role_button_tab },
        { value: AccessMode.custom, description: translation.role_base_access_custom_button_tab }
      ];

      return <div key={formName}>
        <Tabs
          id={formName}
          options={tabsOptions}
          selectedOption={tabsOptions.find(option => option.value === this.state.tab)}
          readOnly={readOnly}
          onChange={this.onChangeTab}
        >
          {this.state.tab === AccessMode.role
            ? this.renderRoleBasedMode()
            : this.renderCustomMode()}
        </Tabs>
      </div>;
    }
  };
};
